<template>
  <main role="main" class="site-content grid-container">
    <div class="section-header section-header--xl section-header--dividing mt-3">
      <h1 class="heading">{{ getBaseProperty('consumerReturnsTitle') }}</h1>
    </div>
    <p class="mb-1">{{ getBaseProperty('consumerReturnsDetails1') }}</p>
    <p class="mb-1">{{ getBaseProperty('consumerReturnsDetails2') }}</p>
    <p class="mb-1">{{ getBaseProperty('consumerReturnsDetails3') }}</p>
    <p class="mb-1">{{ getBaseProperty('consumerReturnsDetails4') }}</p>

    <div class="section-header section-header--md mt-4">
      <h3 class="heading">{{ getBaseProperty('consumerInfo') }}</h3>
    </div>
    <form
      id="powf_544461EAB03CE51180DD3863BB3CE4B0"
      ref="productReturnForm"
      modelAttribute="customerReturnsForm"
      action="confirmation"
      method="post"
      qaid="return-form"
      @submit.prevent="validateAndSubmit"
    >
      <div class="grid-x grid-margin-x">
        <div class="cell medium-6 large-4">
          <input
            id="dealerId"
            v-model="dealerId"
            path="dealerId"
            type="hidden"
            name="dealerId"
            maxlength="100"
            qaid="return-form-dealerId"
          />

          <form-group
            id="firstName"
            v-model="firstNameField"
            v-validate="'required'"
            type="text"
            :data-vv-as="getBaseProperty('firstName')"
            :label="getBaseProperty('firstName')"
            :error="errors.first('firstName')"
            name="firstName"
            :maxlength="100"
            :show-error-messages="true"
            qaid="return-form-firstName"
          ></form-group>
        </div>
        <div class="cell medium-6 large-4">
          <form-group
            id="lastName"
            v-model="lastNameField"
            v-validate="'required'"
            type="text"
            :data-vv-as="getBaseProperty('lastName')"
            :label="getBaseProperty('lastName')"
            :error="errors.first('lastName')"
            name="lastName"
            :maxlength="100"
            :show-error-messages="true"
            qaid="return-form-lastName"
          ></form-group>
        </div>
      </div>

      <div class="grid-x grid-margin-x">
        <div class="cell medium-6 large-4">
          <form-group
            id="email"
            v-model="emailField"
            v-validate="'required|email'"
            type="email"
            :data-vv-as="getBaseProperty('email')"
            :label="getBaseProperty('email')"
            :error="errors.first('email')"
            name="email"
            :maxlength="100"
            :show-error-messages="true"
            qaid="return-form-email"
          ></form-group>
        </div>
        <div class="cell medium-6 large-4">
          <form-group
            id="phoneNumber"
            v-model="phoneNumberField"
            v-validate="'required'"
            type="text"
            :data-vv-as="getBaseProperty('phoneNumber')"
            :label="getBaseProperty('phoneNumber')"
            :error="errors.first('phoneNumber')"
            name="phoneNumber"
            :show-error-messages="true"
            :maxlength="100"
            qaid="return-form-phoneNumber"
          ></form-group>
        </div>
      </div>

      <div class="section-header section-header--md mt-4">
        <h3 class="heading">{{ getBaseProperty('orderInformation') }}</h3>
      </div>
      <div class="grid-x grid-margin-x">
        <div class="cell medium-6 large-4">
          <div class="form-group is-required">
            <form-group
              id="customerPO"
              v-model="customerPoField"
              v-validate="'required'"
              type="text"
              :data-vv-as="getBaseProperty('customerPO')"
              :label="getBaseProperty('customerPO')"
              :error="errors.first('customerPO')"
              name="customerPO"
              :show-error-messages="true"
              :maxlength="100"
              qaid="return-form-customerPO"
            ></form-group>

            <span id="simple-input-help" class="form-help">{{ getBaseProperty('customerPODescription') }}</span>
          </div>
        </div>
      </div>

      <div class="section-header section-header--md mt-4">
        <h3 class="heading">{{ getBaseProperty('consumerReturnsItems') }}</h3>
      </div>
      <template v-for="(item, index) in itemCount">
        <product-return-row
          :key="index"
          :base-properties="baseProperties"
          :row-data="items[index]"
          :row-index="index"
          :validate-fields="childrenValidationTriggered"
          qaid="return-form-rows"
          @deleteRow="removeItem"
          @validationResult="childValidationResult"
        ></product-return-row>
      </template>

      <trek-button v-if="itemCount < maxItemsAllowed" text underline qaid="return-form-addItem" @click="addItem">
        <span v-html="getBaseProperty('addAnotherItem')"></span>
      </trek-button>

      <div class="grid-x grid-margin-x mt-3">
        <div class="cell large-6">
          <div class="form-group">
            <form-group
              id="notes"
              v-model="notesField"
              type="textArea"
              :label="getBaseProperty('consumerReturnsNotes')"
              name="notes"
              :maxlength="500"
              qaid="return-form-notes"
            ></form-group>

            <span id="textarea-help" class="form-help">{{ getBaseProperty('consumerReturnsAdditionalInfo') }}</span>
          </div>
        </div>
      </div>

      <div class="buttons pb-5">
        <trek-button primary type="submit" qaid="return-form-submit">
          <span v-html="getBaseProperty('submit')"></span>
        </trek-button>
        <trek-link button secondary :href="`${encodedContextPath}/`" qaid="return-form-cancel">
          <span v-html="getBaseProperty('cancel')"></span>
        </trek-link>
      </div>
    </form>
  </main>
</template>

<script>
import TrekButton from '@/components/TrekButton';
import TrekLink from '@/components/TrekLink';

export default {
  components: {TrekButton, TrekLink},
  props: {
    baseProperties: {
      type: Array,
      default: () => [],
    },
    dealerId: {
      type: String,
      default: '',
    },
    encodedContextPath: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      itemCount: 1,
      maxItemsAllowed: 5,
      items: [{}],
      firstNameField: '',
      lastNameField: '',
      emailField: '',
      phoneNumberField: '',
      customerPoField: '',
      notesField: '',

      nativeElementsValidationResult: false,
      childrenElementsValidationResult: false,
      childrenValidationTriggered: false,
      validatedRowsCount: 0,
    };
  },

  computed: {
    submitReady() {
      return this.nativeElementsValidationResult && this.childrenElementsValidationResult;
    },
  },

  watch: {
    submitReady(value) {
      if (value === true) {
        this.$refs.productReturnForm.submit();
      }
    },
  },

  methods: {
    validateAndSubmit() {
      // Step 1: validate the native fields (not inside child elements)
      this.$validator.validateAll().then((result) => {
        this.nativeElementsValidationResult = result;
      });

      // Step 2: validate child elements (rows)
      this.validatedRowsCount = 0;
      this.childrenValidationTriggered = true;
    },
    childValidationResult(result) {
      if (result) {
        this.validatedRowsCount++;
        if (this.validatedRowsCount == this.itemCount) {
          // All child rows have been validated
          this.childrenElementsValidationResult = true;
          this.childrenValidationTriggered = false;
        }
      } else {
        this.childrenValidationTriggered = false;
      }
    },
    addItem() {
      if (this.itemCount < this.maxItemsAllowed) {
        this.itemCount++;
        this.items.push({});
      }
    },
    removeItem(index) {
      if (this.itemCount > 1) {
        this.itemCount--;
        this.$delete(this.items, index);
      }
    },
  },
};
</script>
